/*
 * One release note on a page of its own: /releasenotes/{productSlug}/{version}
 * (S42, 2026-10-12).
 *
 * The owner's answer: every note also gets its own page, e.g.
 * /releasenotes/accoplanning/v1.0.87.3, and the list links each title to it.
 * There is one note per product and version (UQ_ReleaseNote_ProductId_Version),
 * so that pair IS the address - a note has no slug of its own - and both route
 * parameters go to the read. The note arrives from kb.ReleaseNotePublished, so a
 * hidden, scheduled or unwritten note is a 404 here, exactly as an unpublished
 * article is on ArticlePage, whose shape this page mirrors: breadcrumb, h1, meta
 * row, the body, and a sidebar with "On this page" and "Signed in as".
 *
 * The breadcrumb is two steps, because a reader arriving from a shared link
 * usually wants one of two things next: every release note, or this product's.
 * The second is the list filtered to the product, which travels as
 * `?products=<id>` - why the public note carries its product's id at all.
 *
 * Images and the body follow the article page's rules: the image src is built
 * from the note's canonical address as the response gives it (the route may have
 * been typed in another case), memoised so the body is not re-parsed on every
 * render; and the body is rendered without video embeds, as everywhere a
 * release note is shown.
 */

import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import {
  RELEASE_NOTES_PATH,
  extractHeadings,
  publicReleaseNoteImagePath,
  releaseNotesHref,
} from '@accotool-kb/shared';
import { getReleaseNote } from '../api/publicApi.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import { DateTime, ErrorBanner, Loading, Tag } from '../components/ui.js';
import { Markdown } from '../components/Markdown.js';
import { usePublicContext } from './PublicLayout.js';
import { ApiError } from '../api/client.js';
import { SignedInAs } from '../site/SignedInAs.js';
import { releaseNotesHeading } from './pageHeadings.js';
import { useLanguage } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';

/** /releasenotes/{productSlug}/{version}: one live release note on a page of its own. */
export function ReleaseNotePage(): JSX.Element {
  const params = useParams();
  const { bootstrap } = usePublicContext();
  const { language } = useLanguage();
  const t = language.messages;

  const productSlug = params.productSlug ?? '';
  const version = params.version ?? '';

  // As the reader (S46): its links to private articles resolve for their audiences.
  const reader = usePublicReader();
  const state = useAsync(
    (signal) => forReader(reader, () => getReleaseNote(productSlug, version, signal, reader)),
    [productSlug, version, reader.ready, reader.key],
  );
  const note = state.data;

  // The canonical pair once the response has it; the route's spelling until then.
  const imageProductSlug = note?.product.slug ?? productSlug;
  const imageVersion = note?.version ?? version;
  const buildImageSrc = useCallback(
    (fileName: string) => publicReleaseNoteImagePath(imageProductSlug, imageVersion, fileName),
    [imageProductSlug, imageVersion],
  );

  useEffect(() => {
    if (note) document.title = `${note.title} · ${bootstrap.site.title}`;
    return () => {
      document.title = bootstrap.site.title;
    };
  }, [note, bootstrap.site.title]);

  // A #fragment is in the URL before the body it points into has rendered.
  useEffect(() => {
    if (!note || !window.location.hash) return;
    let id: string;
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return; // A malformed escape names nothing on this page.
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [note]);

  if (state.loading && !note) return <Loading label={t.releaseNotes.loading} />;

  if (state.error) {
    // A 404 is an answer, not a failure: retrying would only ask again.
    const notFound = state.error instanceof ApiError && state.error.status === 404;
    return (
      <>
        <ErrorBanner error={state.error} {...(notFound ? {} : { onRetry: state.reload })} />
        <p>
          <Link to={RELEASE_NOTES_PATH}>{t.releaseNotes.back}</Link>
        </p>
      </>
    );
  }

  if (!note) return <Loading />;

  const headings = extractHeadings(note.content).filter((heading) => heading.level <= 3);

  return (
    <article>
      <header className="article-header">
        <nav className="breadcrumb" aria-label={t.common.breadcrumb}>
          {/* The list's configured title (2026-10-15), as its own heading reads. */}
          <Link to={RELEASE_NOTES_PATH}>{releaseNotesHeading(bootstrap.site, language.code).title}</Link>
          <span aria-hidden="true">›</span>
          <Link to={releaseNotesHref([note.product.productId])}>{note.product.name}</Link>
        </nav>

        <h1>{note.title}</h1>

        <div className="article-meta">
          <span>{richText(t.common.publishedOn, { date: <DateTime value={note.publishAtUtc} dateOnly /> })}</span>
        </div>

        <div className="tag-list" style={{ marginTop: '0.75rem' }}>
          <Tag kind="product">{note.product.name}</Tag>
          <Tag>{note.version}</Tag>
        </div>
      </header>

      <div className="article-body">
        <div className="article-column">
          <Markdown
            content={note.content}
            linkTargets={note.linkTargets}
            buildImageSrc={buildImageSrc}
            videos={false}
          />
        </div>

        <aside className="sidebar">
          {headings.length > 2 && (
            <nav className="card card-tight sidebar-group" aria-label={t.common.onThisPage}>
              <h3>{t.common.onThisPage}</h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem' }}>
                {headings.map((heading) => (
                  <li
                    key={heading.id}
                    style={{ paddingLeft: `${(heading.level - 1) * 0.75}rem`, marginBottom: 4 }}
                  >
                    <a href={`#${heading.id}`}>{heading.text}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <SignedInAs />
        </aside>
      </div>
    </article>
  );
}
